import { ReactNode } from 'react';
import { OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';
import { MdHelpOutline } from 'react-icons/md';
import cn from 'classnames';
import { InteractiveSpan } from '@Components/InteractiveEl';
import { Spinner } from '@Components/Spinner/Spinner';
import { ModelTypeType } from '../data-qa-constants';
import { ModelCapsule } from './ModelCapsule';
import { ReviewNoticeKind, useReviewNotice } from './ReviewNoticeContext';

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  modelType?: ModelTypeType;
  // Caller passes literal <li> elements; the header lists them in a "How to review"
  // popover.
  tips?: ReactNode;
}

// Shared header for the review sections: title with an optional model capsule and
// cross-model notice chip (e.g. "Same data as Brand Equity"), a one-line subtitle, and
// the QA instructions behind a "How to review" popover so the data starts right below.
export const SectionHeader = ({ title, subtitle, modelType, tips }: SectionHeaderProps) => (
  <header className='data-qa-sechead'>
    <div className='data-qa-sechead__title-row'>
      <h2 className='data-qa-sechead__title'>{title}</h2>
      {modelType && <ModelCapsule modelType={modelType} />}
      <NoticeChip />
      {tips && (
        <OverlayTrigger
          trigger='click'
          rootClose
          placement='bottom-start'
          overlay={
            <Popover className='data-qa-sechead-tips'>
              <Popover.Header>QA Details and Instructions</Popover.Header>
              <Popover.Body>
                <ul>{tips}</ul>
              </Popover.Body>
            </Popover>
          }
        >
          <InteractiveSpan className='data-qa-sechead__how-to'>
            <MdHelpOutline />
            How to review
          </InteractiveSpan>
        </OverlayTrigger>
      )}
    </div>
    {subtitle && <p className='data-qa-sechead__subtitle'>{subtitle}</p>}
  </header>
);

// The full sentence is in a tooltip on a focusable chip, so it's reachable by keyboard
// and by tap, not only by mouse hover. "Checking…" says all it needs to in its label.
const NoticeChip = () => {
  const notice = useReviewNotice();
  if (!notice) return null;

  const className = cn('data-qa-sechead__notice', `data-qa-sechead__notice--${notice.kind}`);
  if (notice.kind === ReviewNoticeKind.CHECKING) {
    return (
      <span className={className} role='status'>
        <Spinner size={12} />
        {notice.label}
      </span>
    );
  }

  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      placement='bottom-start'
      overlay={<Tooltip>{notice.detail}</Tooltip>}
    >
      <InteractiveSpan className={className}>{notice.label}</InteractiveSpan>
    </OverlayTrigger>
  );
};
